import {
    ConversationMemberTypeEnumType,
    ConversationMemberView,
    ConversationRecipientSearchResultView,
} from '@mark43/cad-api';
import React from 'react';
import { Input } from '@cad-arc';
import {
    AsyncMultiSelect,
    AsyncMultiSelectImplementationProps,
} from '../../../forms/inputs/async-multi-select';
import {
    RECIPIENTS_SEARCH_DEBOUNCE_MILLIS,
    RECIPIENTS_SEARCH_MIN_QUERY_LENGTH,
} from '../../constants';
import { useRecipientsQuickSearch } from '../../hooks/use-recipients-quick-search/use-recipients-quick-search';

type ConversationMemberCategory = {
    key: keyof ConversationRecipientSearchResultView;
    label: string;
    entityType: ConversationMemberTypeEnumType | 'ALL_ONLINE';
};

// define recipient select options order and label
const CONVERSATION_MEMBER_CATEGORIES: ConversationMemberCategory[] = [
    { key: 'messagingGroups', label: 'Messaging Groups', entityType: 'MESSAGE_GROUP' },
    { key: 'dispatchAreas', label: 'Dispatch Area Owners', entityType: 'DISPATCH_AREA' },
    {
        key: 'dispatchAreaResponders',
        label: 'Dispatch Area Responders',
        entityType: 'DISPATCH_AREA_RESPONDERS',
    },
    {
        key: 'allOnline',
        label: 'All Online',
        entityType: 'ALL_ONLINE',
    },
    { key: 'callTakerStations', label: 'Call Taker Stations', entityType: 'CALL_TAKER_STATION' },
    { key: 'units', label: 'Units', entityType: 'UNIT' },
    { key: 'users', label: 'Users', entityType: 'USER' },
];
export type RecipientsSelectOption = {
    value: number;
    label: string;
    recipient: ConversationMemberView;
};

type RecipentsSelectProps = Omit<AsyncMultiSelectImplementationProps, 'minimumQueryLength'>;

export const RecipientsSelect = React.forwardRef<HTMLDivElement, RecipentsSelectProps>(
    function RecipientsSelect(props: RecipentsSelectProps, ref): React.ReactElement {
        const {
            isFetchingRecipientLists,
            isErrorFetchingRecipientLists,
            queryRecipients,
        } = useRecipientsQuickSearch();

        // @ts-expect-error Type 'undefined' is not assignable to type 'SelectOptionWithAdditionalData<number>'. return array will not have undefined element
        const getOptions: (
            query: string
        ) => Promise<
            {
                label: string;
                options: RecipientsSelectOption[];
            }[]
        > = React.useCallback(
            (query: string) => {
                try {
                    const searchResult = queryRecipients(query);
                    return CONVERSATION_MEMBER_CATEGORIES.map(
                        function convertRecipientSearchResultsToSelectOptions({
                            key,
                            label,
                        }: ConversationMemberCategory) {
                            const categoryRecipients = searchResult?.[key] ?? [];
                            if (categoryRecipients && categoryRecipients.length) {
                                return {
                                    label,
                                    options: categoryRecipients.map((recipient) => ({
                                        value: recipient.id,
                                        label: recipient.label,
                                        recipient,
                                    })),
                                };
                            }
                            return undefined;
                        }
                    ).filter((option) => !!option);
                } catch {
                    return [];
                }
            },
            [queryRecipients]
        );

        if (isFetchingRecipientLists) {
            return <Input isLoading readOnly placeholder="Loading the recipients list..."></Input>;
        }

        if (isErrorFetchingRecipientLists) {
            return <Input readOnly></Input>;
        }

        return (
            <AsyncMultiSelect
                {...props}
                ref={ref}
                getOptions={getOptions}
                showSelectedGroup={false}
                minimumQueryLength={RECIPIENTS_SEARCH_MIN_QUERY_LENGTH}
                debounceTimer={RECIPIENTS_SEARCH_DEBOUNCE_MILLIS}
                truncateSelectedCount={10}
            />
        );
    }
);
